import { db } from "../../../data/db/db";
import {
  ArticleCuttingSpecsStd,
  ArticleCuttingSpecsTro,
  ArticleSpecs,
} from "../../../models/articleModel";
import { Material } from "../../../models/materialModel";
import { CombinedCalculatorForm, CombinedTrocutFormData, PressureAngles } from "./FormTypes";
import { getEingriffswinkelKorrekturFaktor, getMatchingArticleSpecBasedOnMaterialAndSpec, getPressureAngles } from "./formHelpers";
import i18next from "i18next";

const pi: number = 3.1416;
const kc11 = {
  p1: 1600,
  p2: 2100,
  p3: 2500,
  p4: 3000,
  m1: 1900,
  m2: 2400,
  k1: 875,
  k2: 1830,
  n1: 600,
  n2: 700,
  n3: 1000,
  s1: 1400,
  s2: 2900,
  h1: 3000,
  h2: 3700,
  h3: 4300,
};
const mc = {
  p1: 0.23,
  p2: 0.02,
  p3: 0.25,
  p4: 0.25,
  m1: 0.21,
  m2: 0.21,
  k1: 0.28,
  k2: 0.28,
  n1: 0.25,
  n2: 0.25,
  n3: 0.25,
  s1: 0.25,
  s2: 0.25,
  h1: 0.25,
  h2: 0.25,
  h3: 0.25,
};

const spindleEfficiency = {
  motor: 0.9,
  getriebe: 0.8,
};

export const calculateResult = async (data: CombinedCalculatorForm) => {
  let {
    ident,
    pos,
    material,
    ae,
    ap,
    maschineSpeed,
    spindle,
  } = data;

  let error: string = "";
  let warning: string = "";

  const articleSpecs: ArticleSpecs | undefined = await db.articlesSpec
    .where({ ident: ident, pos: pos })
    .first();
  const articleCuttingSpecs: ArticleCuttingSpecsStd | null =
    await getMatchingArticleSpecBasedOnMaterialAndSpec(
      articleSpecs!,
      material.gruppe
    );

  if (articleSpecs !== undefined && articleCuttingSpecs !== null) {
    //Beheben von falschen Datentypen wegen MUI Textfeldern
    ae = Number(ae);
    ap = Number(ap);
    if (maschineSpeed !== undefined) {
      maschineSpeed = Number(maschineSpeed);
    }

    //Beheben von falschen Datentypen aus API
    articleSpecs.d1 = Number(articleSpecs.d1);
    articleSpecs.d2 = Number(articleSpecs.d2);
    articleSpecs.z = Number(articleSpecs.z);

    //hm
    let hm: number = getStandardHm(articleCuttingSpecs, articleSpecs)

    // Manipulate Values for special cases
    // für alle weiteren Berechnungen gedacht?????
    switch (ident) {
      case "209 30 04":
      case "209 30 14C":
        articleSpecs.d1 = articleSpecs.d2;
        break;
      case "200 00 02C":
      case "200 00 04C":
        ae = 0.1 * articleSpecs.d1;
        break;
    }

    //fz
    let fz: number = getStandardFz(ae, articleSpecs, articleCuttingSpecs, hm);


    //vc
    let vc: number = getStandardVc(ae, articleSpecs, articleCuttingSpecs, material);

    //n
    let n: number = 0;
    if (vc === 0) {
      error = i18next.t("calculatorForm.StdParameterForm.calcError");
    } else {
      if (maschineSpeed === undefined) {
        n = (vc * 1000) / (articleSpecs.d1 * pi);
      } else {
        n = maschineSpeed;
        let newVc = (n * articleSpecs.d1 * pi) / 1000;
        if (newVc > vc) {
          warning =
            i18next.t("calculatorForm.StdParameterForm.calcWarning");
        }
        vc = newVc;
      }
    }

    // Vf
    let vf: number = fz * articleSpecs.z * n;

    // q + Pc + Pmot
    let q: number = (ae * ap * vf) / 1000;
    let kc: number =
      Math.pow(hm, -1 * mc[material.gruppe.toLowerCase() as keyof typeof mc]) *
      kc11[material.gruppe.toLowerCase() as keyof typeof kc11];
    let pc: number = (ae * ap * vf * kc) / (60 * 1000000);
    let pmot: number =
      pc / spindleEfficiency[spindle as keyof typeof spindleEfficiency];
    if (
      articleSpecs.ttype === "wr" ||
      articleSpecs.ttype === "hr" ||
      articleSpecs.ttype === "fhr"
    ) {
      pc *= 0.7;
      pmot *= 0.7;
    }

    //m
    const m: number = ((pc * 30 * 1000) / (n * pi)) * 1.05;

    const result = {
      vc: Math.round(vc + Number.EPSILON),
      hm: Math.round((hm + Number.EPSILON) * 1000) / 1000,
      n: Math.round(n + Number.EPSILON),
      fz: Math.round((fz + Number.EPSILON) * 1000) / 1000,
      vf: Math.round((vf + Number.EPSILON) * 10) / 10,
      pc: pc,
      pmot: Math.round((pmot + Number.EPSILON) * 1000) / 1000,
      q: Math.round((q + Number.EPSILON) * 1000) / 1000,
      m: Math.round((m + Number.EPSILON) * 1000) / 1000,
      kc11: kc11[material.gruppe.toLowerCase() as keyof typeof kc11],
      mc: mc[material.gruppe.toLowerCase() as keyof typeof mc],
      error: error,
      warning: warning,
    };

    return result;
  }

  return null;
};

const getHmForD = (
  hmVal1: number,
  hmD1: number,
  hmVal2: number,
  hmD2: number,
  hmDX: number
): number => {
  const m = (hmVal2 - hmVal1) / (hmD2 - hmD1);
  const b = hmVal1 - m * hmD1;
  const hmValX = m * hmDX + b;
  return hmValX;
};

export const calculateTrocutResult = async (data: CombinedTrocutFormData) => {
  let {
    ident,
    pos,
    material,
  } = data;


  const articleSpecs: ArticleSpecs | undefined = await db.articlesSpec
    .where({ ident: ident, pos: pos })
    .first();

  const articleCuttingSpecsTro: ArticleCuttingSpecsTro | undefined = await db.articleCuttingSpecsTro.where({ ident: ident }).first();

  const articleCuttingSpecs: ArticleCuttingSpecsStd | null =
    await getMatchingArticleSpecBasedOnMaterialAndSpec(
      articleSpecs!,
      material.gruppe
    );

  if (articleSpecs !== undefined && articleCuttingSpecs !== null && articleCuttingSpecsTro !== undefined) {


    // Beheben von falschen Datentypen aus API
    articleSpecs.d1 = Number(articleSpecs.d1);
    articleSpecs.d2 = Number(articleSpecs.d2);
    articleSpecs.z = Number(articleSpecs.z);

    // ae
    let ae: number
    if (articleCuttingSpecsTro.kf_fz_basis === "ae010") {
      ae = 0.1 * articleSpecs.d1
    } else {
      ae = 0.05 * articleSpecs.d1
    }
    let ae_vsp = 1 * articleSpecs.d1;

    // Eingriffswinkel min, max und Korrekturfaktor
    const kfPressureAngles = getEingriffswinkelKorrekturFaktor(articleSpecs.d1, articleSpecs.l2, articleSpecs.serie !== null, articleSpecs.z)
    const pressureAngles: PressureAngles = getPressureAngles(material.gruppe.toLowerCase())

    // hm standard
    let hm: number = getStandardHm(articleCuttingSpecs, articleSpecs)

    //fz standard
    let fz: number = getStandardFz(ae, articleSpecs, articleCuttingSpecs, hm);
    let fz_vsp: number = getStandardFz(ae_vsp, articleSpecs, articleCuttingSpecs, hm);

    //vc standard
    let vc: number = getStandardVc(ae, articleSpecs, articleCuttingSpecs, material);


    let hmax = fz_vsp * articleCuttingSpecsTro.kf_msd;
    let fz_tro = fz * articleCuttingSpecsTro.kf_fz;
    let vc_tro = vc * articleCuttingSpecsTro.kf_vc;
    let pressureAngleMin = pressureAngles.min * kfPressureAngles;
    let pressureAngleMax = pressureAngles.max * kfPressureAngles;
    let aePressureAngleMin = articleSpecs.d1 * pressureAngleMin / 360;
    let aePressureAngleMax = articleSpecs.d1 * pressureAngleMax / 360;

    const result = {
      hmax: Math.round((hmax + Number.EPSILON) * 1000) / 1000,
      fz_tro: Math.round((fz_tro + Number.EPSILON) * 1000) / 1000,
      vc_tro: Math.round(vc_tro + Number.EPSILON),
      pressureAngleMin: Math.round((pressureAngleMin + Number.EPSILON) * 10) / 10,
      pressureAngleMax: Math.round((pressureAngleMax + Number.EPSILON) * 10) / 10,
      aePressureAngleMin: Math.round((aePressureAngleMin + Number.EPSILON) * 1000) / 1000,
      aePressureAngleMax: Math.round((aePressureAngleMax + Number.EPSILON) * 1000) / 1000,
    }

    return result;

  }

  return null
}

const getStandardHm = (articleCuttingSpecs: ArticleCuttingSpecsStd, articleSpecs: ArticleSpecs) => {
  let hm: number = 0;
  if (articleCuttingSpecs.hm_typ === "mic") {
    hm = getHmForD(
      articleCuttingSpecs.hm001,
      0.1,
      articleCuttingSpecs.hm02,
      2,
      articleSpecs.d1
    );
  } else {
    hm = getHmForD(
      articleCuttingSpecs.hm06,
      6,
      articleCuttingSpecs.hm10,
      10,
      articleSpecs.d1
    );
  }

  return hm;
}

const getStandardFz = (ae: number, articleSpecs: ArticleSpecs, articleCuttingSpecs: ArticleCuttingSpecsStd, hm: number) => {
  let fz: number = 0;
  const fzBase = hm / Math.sqrt(ae / articleSpecs.d1);
  if (ae === articleSpecs.d1) {
    fz = fzBase * articleCuttingSpecs.vsq; // Vollspurfaktor
  } else {
    fz = fzBase * articleCuttingSpecs.allgq; // Allgemeiner Faktor
  }
  return fz
}

const getStandardVc = (ae: number, articleSpecs: ArticleSpecs, articleCuttingSpecs: ArticleCuttingSpecsStd, material: Material) => {
  let vc: number = 0;
  if (ae / articleSpecs.d1 <= 0.3 && articleCuttingSpecs.vc00_flag === 1) {
    let str = ("vc00_" +
      material.gruppe.toLowerCase()) as keyof typeof articleCuttingSpecs;
    /* @ts-ignore */
    vc = articleCuttingSpecs[str];
  } else {
    let str = ("vc01_" +
      material.gruppe.toLowerCase()) as keyof typeof articleCuttingSpecs;
    /* @ts-ignore */
    vc = articleCuttingSpecs[str];
  }

  return vc;
}